import React, { useState, useEffect } from "react";
import Swal from 'sweetalert2';

import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Toolbar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom"; // Importa o useNavigate
import SideBar from "../../dashboard admin/components/SideBar";
import api from "../../../../services/api/api"; // Aqui você importa o arquivo de configuração da API

const CategoriesAdmin = () => {
  const [search, setSearch] = useState("");
  const [categories, setCategories] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);  // Controla o pop-up de confirmação
  const [categoryIdToDelete, setCategoryIdToDelete] = useState(null);  // Armazena o ID da categoria a ser excluída
  const navigate = useNavigate(); // Inicializa o hook useNavigate

  // Função para obter o token do localStorage ou de onde você o armazena
  const getAuthToken = () => {
    return localStorage.getItem("token"); // ou sessionStorage.getItem("authToken")
  };

  // Buscar categorias da API
  useEffect(() => {
    const token = getAuthToken();
    
    if (token) {
      api.get('api/categories', {
          headers: {
            Authorization: `Bearer ${token}`, // Adiciona o token no cabeçalho
          },
        })
        .then((response) => {
          // Atualiza o estado com as categorias retornadas
          setCategories(response.data);
        })
        .catch((error) => {
          console.error("Erro ao buscar categorias:", error);
          // Mostrar erro com SweetAlert2
          Swal.fire({
            icon: 'error',
            title: 'Erro!',
            text: 'Não foi possível carregar as categorias.',
            confirmButtonText: 'OK'
          });
        });
    } else {
      console.error("Token de autenticação não encontrado.");
      // Mostrar erro de autenticação com SweetAlert2
      Swal.fire({
        icon: 'warning',
        title: 'Atenção!',
        text: 'Token de autenticação não encontrado.',
        confirmButtonText: 'OK'
      });
    }
  }, []); // O array vazio [] faz com que o efeito seja executado uma vez ao montar o componente

  // Lidar com a busca
  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  // Filtrar a lista de categorias
  const filteredCategories = categories.filter((category) =>
    category.name.toLowerCase().includes(search.toLowerCase())
  );

  // Função para excluir categoria
  const handleDeleteCategory = () => {
    const token = getAuthToken();

    if (!token) {
      console.error("Token de autenticação não encontrado.");
      Swal.fire({
        icon: 'warning',
        title: 'Atenção!',
        text: 'Token de autenticação não encontrado.',
        confirmButtonText: 'OK'
      });
      return;
    }

    api
      .delete(`api/deleteCategory/${categoryIdToDelete}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        // Remove a categoria excluída da lista
        setCategories(categories.filter((category) => category._id !== categoryIdToDelete));
        
        // Mostrar sucesso com SweetAlert2
        Swal.fire({
          icon: 'success',
          title: 'Sucesso!',
          text: 'Categoria excluída com sucesso!',
          confirmButtonText: 'OK',
          timer: 2000,
          timerProgressBar: true
        });
        
        setOpenDialog(false);  // Fecha o pop-up após excluir
      })
      .catch((error) => {
        console.error("Erro ao excluir categoria:", error);
        
        // Mostrar erro com SweetAlert2
        Swal.fire({
          icon: 'error',
          title: 'Erro!',
          text: 'Não foi possível excluir a categoria.',
          confirmButtonText: 'OK'
        });
        
        setOpenDialog(false);  // Fecha o pop-up em caso de erro
      });
  };

  // Função alternativa usando SweetAlert2 para confirmação (substitui o Dialog do Material-UI)
  const handleDeleteCategoryWithSwal = async (catId) => {
    const result = await Swal.fire({
      title: 'Tem certeza?',
      text: 'Você não poderá reverter esta ação!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sim, excluir!',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      const token = getAuthToken();

      if (!token) {
        console.error("Token de autenticação não encontrado.");
        Swal.fire({
          icon: 'warning',
          title: 'Atenção!',
          text: 'Token de autenticação não encontrado.',
          confirmButtonText: 'OK'
        });
        return;
      }

      try {
        await api.delete(`api/deleteCategory/${catId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        // Remove a categoria excluída da lista
        setCategories(categories.filter((category) => category._id !== catId));
        
        // Mostrar sucesso
        Swal.fire({
          icon: 'success',
          title: 'Excluída!',
          text: 'A categoria foi excluída com sucesso.',
          confirmButtonText: 'OK',
          timer: 2000,
          timerProgressBar: true
        });

      } catch (error) {
        console.error("Erro ao excluir categoria:", error);
        
        // Mostrar erro
        Swal.fire({
          icon: 'error',
          title: 'Erro!',
          text: 'Não foi possível excluir a categoria.',
          confirmButtonText: 'OK'
        });
      }
    }
  };

  // Função para redirecionar para a página de edição
  const handleEditCategory = (catId) => {
    navigate(`/admin/editcategory/${catId}`); // Navega para a página de edição
  };

  // Função para abrir o pop-up de confirmação de exclusão
  const handleConfirmDelete = (catId) => {
    setCategoryIdToDelete(catId);  // Define o ID da categoria a ser excluída
    setOpenDialog(true);  // Abre o pop-up
  };

  // Função para cancelar a exclusão
  const handleCancelDelete = () => {
    setOpenDialog(false);  // Fecha o pop-up sem excluir
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", backgroundColor: "#f5f5f5" }}>
      {/* Sidebar */}
      <Box>
        <SideBar />
      </Box>

      {/* Main Content */}
      <Box sx={{ flex: 1, p: 3 }}>
        <Typography variant="h4" fontWeight="bold" mb={3}>
          Categories Management
        </Typography>

        {/* Toolbar */}
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            backgroundColor: "#fff",
            borderRadius: 1,
            boxShadow: 1,
            mb: 2,
            p: 1,
          }}
        >
          <TextField
            size="small"
            variant="outlined"
            placeholder="Search categories..."
            value={search}
            onChange={handleSearch}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: "gray" }} />,
            }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              background: "linear-gradient(90deg, #1cb5e0, #000851)",
              textTransform: "none",
            }}
            href="createcategory"
          >
            Add Category
          </Button>
        </Toolbar>

        {/* Categories Table */}
        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Creation Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '10%' }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCategories.map((category) => (
                <TableRow key={category._id}>
                  <TableCell>{category.name}</TableCell>
                  <TableCell>{category.description}</TableCell>
                  <TableCell>{new Date(category.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell align="right">
                    <IconButton color="primary" onClick={() => handleEditCategory(category._id)}>
                      <EditIcon />
                    </IconButton>
                    {/* Usando a função com SweetAlert2 diretamente */}
                    <IconButton color="error" onClick={() => handleDeleteCategoryWithSwal(category._id)}>
                      <DeleteIcon />
                    </IconButton>
                    {/* Ou mantendo o Dialog do Material-UI */}
                    {/* <IconButton color="error" onClick={() => handleConfirmDelete(category._id)}>
                      <DeleteIcon />
                    </IconButton> */}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Dialog de Confirmação de Exclusão - Opcional, se quiser manter */}
      <Dialog open={openDialog} onClose={handleCancelDelete}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this category?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteCategory} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategoriesAdmin;